import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import ContactFormModal from '../components/ContactFormModal';

const WebDesignSEO = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const services = [
    { title: 'Author Website Design', description: 'Custom-designed author websites that showcase your books and build your brand.', features: ['Mobile responsive', 'Book showcase pages', 'Email signup forms', 'Blog integration'] },
    { title: 'SEO Optimization', description: 'Search engine optimization to help readers find your books and website online.', features: ['Keyword research', 'On-page SEO', 'Content optimization', 'Technical SEO'] },
    { title: 'E-commerce Integration', description: 'Sell books directly from your website with secure payment processing.', features: ['Shopping cart', 'Payment gateway', 'Digital delivery', 'Order management'] },
    { title: 'Ongoing Maintenance', description: 'Keep your website updated, secure, and performing at its best.', features: ['Security updates', 'Content updates', 'Backup services', 'Performance monitoring'] }
  ];

  const process = [
    { step: '01', title: 'Discovery & Planning', description: 'We discuss your goals, analyze your competition, and create a comprehensive website strategy tailored to your author brand.', icon: 'https://cdn-icons-png.flaticon.com/512/3524/3524659.png' },
    { step: '02', title: 'Design & Development', description: 'Custom design mockups followed by professional development. Your website is built to be fast, secure, and search engine friendly.', icon: 'https://cdn-icons-png.flaticon.com/512/2521/2521826.png' },
    { step: '03', title: 'Launch & Optimize', description: 'We launch your site with proper SEO setup, train you on content management, and provide ongoing optimization to improve search rankings.', icon: 'https://cdn-icons-png.flaticon.com/512/3524/3524388.png' }
  ];

  const whyChooseUs = [
    { icon: 'https://cdn-icons-png.flaticon.com/512/3330/3330315.png', title: 'Author-Focused Design', description: 'Websites specifically designed for authors to showcase books and grow readership.' },
    { icon: 'https://cdn-icons-png.flaticon.com/512/2490/2490402.png', title: 'SEO Expertise', description: 'Proven strategies that get author websites ranking on page 1 of Google.' },
    { icon: 'https://cdn-icons-png.flaticon.com/512/2838/2838779.png', title: 'Fast Loading', description: 'Speed-optimized websites that load in under 2 seconds on mobile.' },
    { icon: 'https://cdn-icons-png.flaticon.com/512/3524/3524715.png', title: 'Easy Management', description: 'User-friendly content management so you can update your site yourself.' }
  ];

  const faqs = [
    { question: 'How much does a custom author website cost?', answer: 'Author websites range from $2,000-8,000 depending on complexity. Basic 5-page site: $2,000-3,000. Advanced site with blog and e-commerce: $4,000-6,000. Premium site with custom features: $6,000-8,000+. Monthly hosting and maintenance: $50-150/month.' },
    { question: 'Do I really need a website if I have social media?', answer: "Yes! You don't own your social media accounts - platforms can change algorithms, ban accounts, or shut down. Your website is the only online presence you truly own. It's also essential for SEO - when readers search your name or books, your website should appear first." },
    { question: 'How long does it take to build an author website?', answer: 'Most author websites are completed in 4-6 weeks: Week 1-2: Discovery, planning, and design mockups. Week 3-4: Development and content integration. Week 5-6: Testing, SEO setup, and launch. Rush projects can be completed in 2-3 weeks.' },
    { question: 'What is SEO and why do I need it?', answer: 'SEO (Search Engine Optimization) helps your website rank higher in Google search results. When readers search for your name, book titles, or topics you write about, SEO ensures your website appears. Good SEO can drive 500-5,000+ monthly visitors to your site organically (free traffic).' },
    { question: 'Will my website be mobile-friendly?', answer: 'Absolutely. All websites we build are fully responsive and mobile-optimized. Over 60% of website traffic comes from mobile devices, and Google prioritizes mobile-friendly sites in search rankings. Your site will look and work perfectly on phones, tablets, and desktops.' },
    { question: 'Can I update the website myself?', answer: 'Yes! We build sites on user-friendly platforms like WordPress. We provide training on how to add blog posts, update book information, and make basic changes. For major updates or redesigns, we are available for ongoing support at affordable rates.' },
    { question: 'What is included in SEO optimization?', answer: 'Our SEO service includes: keyword research for your name and books, on-page optimization (titles, meta descriptions, content), technical SEO (speed, security, mobile-friendliness), Google Search Console setup, sitemap submission, and initial content optimization. Ongoing SEO available monthly.' },
    { question: 'Do you offer website hosting?', answer: 'Yes, we offer managed hosting starting at $50/month including: fast SSD servers, daily backups, security monitoring, SSL certificate (HTTPS), email accounts, and 24/7 support. Or we can help you set up hosting with providers like SiteGround or Bluehost.' }
  ];

  const testimonials = [
    { name: 'Robert Mitchell', title: 'Historical Fiction Author', text: 'My new website is beautiful and professional. Even better - I am now ranking #1 on Google for my name and my books. Traffic has increased 400%.', rating: 5 },
    { name: 'Amanda Stevens', title: 'Romance Author', text: 'The team built exactly what I envisioned. The e-commerce integration lets me sell signed copies directly to readers. It has become a significant income stream!', rating: 5 },
    { name: 'David Kim', title: 'Business Author', text: 'Outstanding SEO work. My author site now ranks for dozens of keywords related to my book topic. It is driving speaking opportunities and consulting clients.', rating: 5 }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="bg-gradient-to-br from-amber-50 to-white pt-24 py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 border-2 border-amber-700 rounded-full mb-6">
                <span className="text-amber-700 text-sm font-semibold tracking-wider">WEB DESIGN & SEO SERVICES</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                Professional Author Websites That <span className="text-amber-700">Get Found</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Custom-designed author websites with powerful SEO that help readers discover you and your books online. Build your platform and grow your readership.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <Button onClick={() => setIsModalOpen(true)} className="bg-amber-700 hover:bg-amber-800 text-white font-bold py-6 px-10 text-lg rounded-full shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all">
                  Get Website Quote
                </Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                <div><div className="text-4xl font-bold text-amber-700 mb-2">300+</div><div className="text-gray-600 text-sm">Sites Built</div></div>
                <div><div className="text-4xl font-bold text-amber-700 mb-2">Page 1</div><div className="text-gray-600 text-sm">Google Rankings</div></div>
                <div><div className="text-4xl font-bold text-amber-700 mb-2">100%</div><div className="text-gray-600 text-sm">Mobile Optimized</div></div>
                <div><div className="text-4xl font-bold text-amber-700 mb-2">4-6</div><div className="text-gray-600 text-sm">Weeks Average</div></div>
              </div>
            </div>
            <div className="relative">
              <div className="relative z-10">
                <img src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop" alt="Web design" className="rounded-2xl shadow-2xl w-full h-[500px] object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Complete <span className="text-amber-700">Web Services</span></h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {services.map((service, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <ul className="space-y-3">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-amber-700 flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">How It <span className="text-amber-700">Works</span></h2>
          </div>
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              {process.map((item, index) => (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all border-t-4 border-amber-500 relative">
                  <div className="absolute -top-6 left-8">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center shadow-lg">
                      <span className="text-2xl font-bold text-white">{item.step}</span>
                    </div>
                  </div>
                  <div className="mt-8 mb-6 flex justify-center">
                    <div className="w-24 h-24 bg-gradient-to-br from-amber-50 to-amber-100 rounded-full flex items-center justify-center p-5">
                      <img src={item.icon} alt={item.title} className="w-full h-full object-contain" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed text-center">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Why Choose <span className="text-amber-700">Rushmore Publishing</span></h2>
              <div className="grid md:grid-cols-2 gap-6">
                {whyChooseUs.map((item, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0 w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center p-3">
                      <img src={item.icon} alt={item.title} className="w-full h-full object-contain" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-600 text-sm">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img src="https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07?w=800&h=600&fit=crop" alt="SEO results" className="rounded-2xl shadow-2xl w-full h-[500px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?w=1920&h=800&fit=crop" alt="Web development" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-amber-900/95 to-amber-700/95"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center text-white">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-5xl md:text-6xl font-bold mb-6">Ready to Build Your Author Website?</h2>
            <p className="text-xl mb-12 opacity-90">Get a free consultation and quote. Let's create a website that grows your author platform.</p>
            <Button onClick={() => setIsModalOpen(true)} className="bg-white hover:bg-gray-100 text-amber-700 font-bold py-6 px-12 text-xl rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all">
              Get Your Free Quote
            </Button>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16"><h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">What Our <span className="text-amber-700">Authors Say</span></h2></div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="flex gap-1 mb-4">{[...Array(testimonial.rating)].map((_, i) => (<span key={i} className="text-amber-500 text-xl">★</span>))}</div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div><div className="font-bold text-gray-900">{testimonial.name}</div><div className="text-sm text-gray-600">{testimonial.title}</div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16"><h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Frequently Asked <span className="text-amber-700">Questions</span></h2></div>
          <div className="max-w-3xl mx-auto">
            {faqs.map((faq, index) => (
              <div key={index} className="mb-4">
                <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full bg-gray-50 hover:bg-gray-100 p-6 rounded-xl text-left flex items-center justify-between transition-all">
                  <span className="font-bold text-gray-900 text-lg pr-4">{faq.question}</span>
                  {openFaq === index ? <ChevronUp className="w-6 h-6 text-amber-700 flex-shrink-0" /> : <ChevronDown className="w-6 h-6 text-amber-700 flex-shrink-0" />}
                </button>
                {openFaq === index && (<div className="bg-white p-6 rounded-b-xl border-l-4 border-amber-700"><p className="text-gray-700 leading-relaxed">{faq.answer}</p></div>)}
              </div>
            ))}
          </div>
        </div>
      </section>

      <ContactFormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default WebDesignSEO;
